import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ErrorHandler,
  inject,
  signal
} from '@angular/core';
import {PageTitleComponent} from "../../../components/page-title/page-title.component";
import {PageSubtitleComponent} from "../../../components/page-subtitle/page-subtitle.component";
import {NotificationCategoryTogglesComponent, NotificationCategoryChange} from "../../../components/notification-category-toggles/notification-category-toggles.component";
import {ToggleSwitch, ToggleSwitchChangeEvent} from "primeng/toggleswitch";
import {FormBuilder, ReactiveFormsModule, Validators} from "@angular/forms";
import {emailValidationErrorMessage, emailValidators} from "../../../validators";
import {NgClass} from "@angular/common";
import {firstValueFrom} from "rxjs";
import {Divider} from "primeng/divider";
import {isNative} from "../../../utils";
import {Dialog} from "primeng/dialog";
import {Button} from "primeng/button";
import {SubmitButtonComponent} from "../../../components/submit-button/submit-button.component";
import {FloatLabel} from "primeng/floatlabel";
import {InputMask} from "primeng/inputmask";
import {InputText} from "primeng/inputtext";
import {SkeletonModule} from "primeng/skeleton";
import {PushNotificationService} from "../../../services/push-notification.service";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {NotificationSettingsService} from "../../../services/notification-settings.service";
import {NotificationSettingsDto} from "homey-housing-lottery-app-dto";
import {environment} from "../../../../../environments/environment";
import {Injector} from "@angular/core";
import {LoadFailedComponent} from "../../../components/load-failed/load-failed.component";

@Component({
  imports: [
    PageTitleComponent,
    PageSubtitleComponent,
    NotificationCategoryTogglesComponent,
    ToggleSwitch,
    Divider,
    ReactiveFormsModule,
    NgClass,
    Dialog,
    Button,
    SubmitButtonComponent,
    FloatLabel,
    InputMask,
    InputText,
    SkeletonModule,
    LoadFailedComponent
  ],
  templateUrl: './account-notifications-page.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountNotificationsPageComponent {
  private readonly fb = inject(FormBuilder);
  protected readonly pushNotificationService = inject(PushNotificationService)
  private readonly notificationSettingsService = inject(NotificationSettingsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly errorHandler = inject(ErrorHandler);
  protected readonly devicePushNotificationsEnabled = toSignal(
    this.pushNotificationService.devicePushPermissionsEnabled$,
    {initialValue: false}
  );
  private readonly phoneValidatorFn = (control: {value: string | null | undefined}) =>
    this.isValidPhone(control.value) ? null : {phoneInvalid: true};

  protected readonly form = this.fb.group({
    emailNotificationsEnabled: this.fb.nonNullable.control<boolean | undefined>(undefined),
    pushNotificationsEnabled: this.fb.nonNullable.control<boolean | undefined>(undefined),
    smsNotificationsEnabled: this.fb.nonNullable.control<boolean | undefined>(undefined),
    // Notifications go to this address: one with no dot in its domain (name@gmail) never arrives.
    email: this.fb.control<string | undefined>(undefined, [Validators.required, ...emailValidators]),
    phone: this.fb.control<string | undefined>(undefined, [Validators.required, this.phoneValidatorFn])
  })

  private smsDialogOpenedFromToggle = false;

  private readonly syncPushToggleEffect = effect(() => {
    const settings = this.settings();
    if (!settings) {
      return;
    }

    const pushChannel = settings.channels.push;
    const pushEnabledOnDevice = this.devicePushNotificationsEnabled();
    // While the toggle enables push, the device permission is granted before the channel is, so leave it alone then
    if (pushEnabledOnDevice && !pushChannel?.enabled) {
      return;
    }
    this.form.patchValue({
      pushNotificationsEnabled: pushEnabledOnDevice
    }, {emitEvent: false});
  }, {injector: this.injector});

  protected readonly loading = signal(true);
  protected readonly settings = signal<NotificationSettingsDto | null>(null);
  protected readonly loadFailed = computed(() => !this.loading() && !this.settings());
  protected readonly retryLoad = () => this.refreshNotificationSettings();
  protected readonly emailValidationErrorMessage = emailValidationErrorMessage;
  // The address notifications go to; the form's email is only the dialog's edit field.
  protected readonly savedEmailAddress = computed(() => this.settings()?.channels.email?.emailAddress);
  // The number texts go to; the form's phone is only the dialogs' edit field.
  protected readonly savedPhoneNr = computed(() => this.settings()?.channels.text?.phoneNr);
  protected readonly emailPopupVisible = signal(false);

  submitPhoneNumber = async () => {
    const phoneControl = this.form.controls.phone;
    phoneControl.markAsTouched();
    phoneControl.updateValueAndValidity();
    if (phoneControl.invalid) {
      return;
    }

    const phoneNr = phoneControl.value ?? '';
    await firstValueFrom(this.notificationSettingsService.updateChannel({
      type: 'text',
      enabled: true,
      phoneNr
    }));

    await this.refreshNotificationSettings();

    this.smsDialogOpenedFromToggle = false;
    this.enableSmsPopupVisible.set(false);
    this.editSmsPopupVisible.set(false);
  }

  submitEmailAddress = async () => {
    const emailControl = this.form.controls.email;
    emailControl.markAsTouched();
    emailControl.updateValueAndValidity();
    if (emailControl.invalid) {
      return;
    }

    const email = (emailControl.value ?? '').trim().toLowerCase();
    await firstValueFrom(this.notificationSettingsService.updateChannel({
      type: 'email',
      enabled: true,
      emailAddress: email
    }));

    await this.refreshNotificationSettings();
    this.emailPopupVisible.set(false);
  }

  protected openEmailEdit() {
    this.emailPopupVisible.set(true);
  }

  // Closed without saving: back to the saved address, so nothing unsaved shows or gets sent later.
  protected onEmailDialogHide() {
    this.form.controls.email.reset(this.savedEmailAddress() ?? '', {emitEvent: false});
  }

  protected openPhoneEdit() {
    this.smsDialogOpenedFromToggle = false;
    this.form.controls.phone.markAsUntouched();
    this.form.controls.phone.updateValueAndValidity({emitEvent: false});
    this.editSmsPopupVisible.set(true);
  }

  protected sendSmsNotificationEnabled(event: ToggleSwitchChangeEvent) {
    if (event.checked) {
      this.smsDialogOpenedFromToggle = true;
      this.form.controls.phone.markAsUntouched();
      this.form.controls.phone.updateValueAndValidity({emitEvent: false});
      this.enableSmsPopupVisible.set(true);
    } else {
      void this.updateTextChannel(false);
    }
  }

  async onPushNotionEnabled(event: ToggleSwitchChangeEvent) {
    await this.updatePushChannel(Boolean(event.checked));
  }

  // Closed without saving: back to the saved number, so nothing unsaved shows or gets sent later.
  protected onPhoneDialogHide() {
    this.form.controls.phone.reset(this.savedPhoneNr() ?? '', {emitEvent: false});
  }

  protected onHide() {
    this.onPhoneDialogHide();
    if (this.smsDialogOpenedFromToggle) {
      this.form.patchValue({
        smsNotificationsEnabled: false
      }, {emitEvent: false});
    }
    this.smsDialogOpenedFromToggle = false;
    this.enableSmsPopupVisible.set(false);
  }

  protected readonly enableSmsPopupVisible = signal(false);
  protected readonly editSmsPopupVisible = signal(false);

  protected readonly isNative = isNative;
  protected readonly pushPermissionDeniedDialogVisible = signal(false);

  constructor() {
    this.loadNotificationSettings();
    this.setupControlSubscriptions();
  }

  protected readonly iosAppUrl = environment.appstoreLinks.ios;
  protected readonly androidAppUrl = environment.appstoreLinks.android;

  private loadNotificationSettings() {
    this.loading.set(true);
    void this.refreshNotificationSettings();
  }

  private async refreshNotificationSettings() {
    try {
      const latest = await firstValueFrom(this.notificationSettingsService.getNotificationSettings());
      this.applySettings(latest);
    } catch (error) {
      this.errorHandler.handleError(error);
    } finally {
      this.loading.set(false);
    }
  }

  private applySettings(settings: NotificationSettingsDto) {
    this.settings.set(settings);

    const emailChannel = settings.channels.email;
    const textChannel = settings.channels.text;
    const pushChannel = settings.channels.push;

    const pushEnabledOnDevice = this.devicePushNotificationsEnabled();
    const shouldEnablePushOnDevice = Boolean(pushChannel?.enabled && pushEnabledOnDevice);

    this.form.patchValue({
      emailNotificationsEnabled: emailChannel?.enabled ?? false,
      smsNotificationsEnabled: textChannel?.enabled ?? false,
      pushNotificationsEnabled: shouldEnablePushOnDevice,
      email: emailChannel?.emailAddress ?? '',
      phone: textChannel?.phoneNr ?? ''
    }, {emitEvent: false});

    const pushControl = this.form.controls.pushNotificationsEnabled;
    if (!isNative) {
      pushControl.disable({emitEvent: false});
    } else {
      pushControl.enable({emitEvent: false});
    }
  }

  private setupControlSubscriptions() {
    this.form.controls.emailNotificationsEnabled.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value === null || value === undefined) return;
        void this.updateEmailChannel(value);
      });

    this.form.controls.phone.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const control = this.form.controls.phone;
        if (!control.touched) return;
        control.updateValueAndValidity({emitEvent: false});
      });

  }

  private async updateEmailChannel(enabled: boolean) {
    // The saved address, not the field: the dialog may have been closed with an unsaved (unchecked) one.
    const emailAddress = this.savedEmailAddress();
    if (!emailAddress) {
      return;
    }

    await firstValueFrom(this.notificationSettingsService.updateChannel({
      type: 'email',
      enabled,
      emailAddress: emailAddress.toLowerCase(),
    }));

    await this.refreshNotificationSettings();
  }

  private async updateTextChannel(enabled: boolean) {
    // The saved number, not the field: a dialog may have been closed with an unsaved (unchecked) one.
    const phoneNr = this.savedPhoneNr();
    if (!phoneNr) {
      return;
    }

    await firstValueFrom(this.notificationSettingsService.updateChannel({
      type: 'text',
      enabled,
      phoneNr,
    }));

    await this.refreshNotificationSettings();
  }

  private async updatePushChannel(enabled: boolean): Promise<boolean> {
    const settings = this.settings();
    if (!settings) {
      return false;
    }

    if (enabled) {
      const result = await this.pushNotificationService.registerNotifications();
      if (!result.granted) {
        this.form.patchValue({pushNotificationsEnabled: false}, {emitEvent: false});
        if (result.status === 'denied') {
          this.pushPermissionDeniedDialogVisible.set(true);
        }
        return false;
      }
    }

    await firstValueFrom(this.notificationSettingsService.updateChannel({
      type: 'push',
      enabled,
      token: undefined
    }));

    await this.refreshNotificationSettings();
    return true;
  }

  protected async onCategoryChange(event: NotificationCategoryChange) {
    await this.updateCategoryPreference(event.category, event.enabled);
  }

  private async updateCategoryPreference(category: 'lottery' | 'lotteryfollowup' | 'promotional', enabled: boolean) {
    const settings = this.settings();
    if (!settings) {
      return;
    }

    await firstValueFrom(this.notificationSettingsService.updatePreference({
      category,
      enabled,
    }));

    await this.refreshNotificationSettings();
  }

  private isValidEmail(email: string | null | undefined) {
    if (!email) return false;
    return /\S+@\S+\.\S+/.test(email);
  }

  private isValidPhone(phone: string | null | undefined) {
    if (!phone) {
      return false;
    }
    // Accept 10 digits regardless of mask formatting
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10;
  }
}
